import React from "react";
import { Box, Typography, Stack, Chip, Tooltip } from "@mui/material";
import StandardButton from "../../../../components/StandardButton";
import moment from "moment";

import AvatarList from "../../../../components/molecules/AvatarList";
import MightyDataGrid from "../../../../components/organisms/MightyDataGrid";
import EmptyClientGroups from "./EmptyClientGroups";
import EmptyCustomGroups from "./EmptyCustomGroups";
import EmptyAlertGroups from "./EmptyAlertGroups";

const GroupTable = ({
  id,
  groups,
  users,
  title,
  loading,
  onAddGroupClicked,
  onGroupClicked,
  editClicked,
}) => {

  const columns = React.useMemo(() => {
    return [
      {
        field: "name",
        headerName: "Group Name",
        width: 250,
        hideable: false,
        headerAlign: "left",
        align: "left",
      },
      {
        field: "users",
        headerName: "Members",
        width: 120,
        headerAlign: "center",
        align: "center",
        filterable: false,
        sortable: false,
        renderCell: (params) => {
          const memberCount = params.row.users?.length || 0;
          const usersData = users
            .filter((u) => {
              return params.row.users.includes(u.uid);
            })
            .map((user) => {
              return {
                key: user.uid,
                url: user.avatarUrl,
                firstName: user.firstName,
                lastName: user.lastName,
              };
            });

          return (
            <Tooltip
              title={`${memberCount} member${memberCount !== 1 ? "s" : ""}`}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <AvatarList users={usersData.slice(0, 3)} size={32} />
                {memberCount > 3 && (
                  <Chip
                    label={`+${memberCount - 3}`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
              </Stack>
            </Tooltip>
          );
        },
      },
      {
        field: "lastMessage",
        headerName: "Last Message",
        minWidth: 250,
        flex: 1,
        headerAlign: "left",
        align: "left",
        renderCell: (params) => {
          const message = params.row.lastMessage;
          if (!message) {
            return (
              <Typography
                variant="body2"
                color="text.secondary"
                style={{ fontStyle: "italic" }}
              >
                No messages yet
              </Typography>
            );
          }

          const truncatedMessage =
            message.length > 60 ? message.substring(0, 60) + "..." : message;

          return (
            <Tooltip title={message} placement="top">
              <Typography variant="body2" style={{ fontSize: "0.875rem" }}>
                {truncatedMessage}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        field: "recentActivity",
        headerName: "Recent Activity",
        width: 150,
        headerAlign: "center",
        align: "center",
        sortable: true,
        valueGetter: (params) => {
          return params.row.lastDate?.toDate() || null;
        },
        renderCell: (params) => {
          if (!params.row.lastUser || !params.row.lastDate) {
            return (
              <Typography
                variant="body2"
                color="text.secondary"
                style={{ fontStyle: "italic" }}
              >
                No activity
              </Typography>
            );
          }

          const timeAgo = moment(params.row.lastDate.toDate()).fromNow();
          const userName = `${params.row.lastUser.firstName} ${params.row.lastUser.lastName}`;

          return (
            <Tooltip
              title={`${userName} at ${moment(params.row.lastDate.toDate()).format("MMM D, h:mm A")}`}
            >
              <Stack alignItems="center" spacing={0.5}>
                <Typography variant="caption" color="text.secondary">
                  {userName}
                </Typography>
                <Typography variant="caption" color="primary">
                  {timeAgo}
                </Typography>
              </Stack>
            </Tooltip>
          );
        },
      },
    ];
  }, [users]);

  const rows = React.useMemo(() => {
    return groups.map((group) => {
      return {
        id: group.uid,
        ...group,
      };
    });
  }, [groups]);

  // Show loading first, then empty state when data confirms no groups
  if (loading) {
    return <Typography>Loading message groups...</Typography>;
  }

  if (groups.length === 0) {
    if (id === "clientGroups") {
      return <EmptyClientGroups />;
    } else if (id === "customGroups") {
      return <EmptyCustomGroups onAddGroup={onAddGroupClicked} />;
    } else if (id === "alertGroups") {
      return <EmptyAlertGroups onAddGroup={onAddGroupClicked} />;
    }
  }

  return (
    <Box style={{ margin: 12, height: "100%" }}>
      <Box display="flex" justifyContent="space-between">
        <Typography variant="h5">{title || "Message Groups"}</Typography>
        {onAddGroupClicked && (
          <StandardButton
            variant="primary"
            color="primary"
            onClick={() => {
              if (onAddGroupClicked) {
                onAddGroupClicked();
              }
            }}
          >
            Add Message Group
          </StandardButton>
        )}
      </Box>

      <MightyDataGrid
        entityName={`groups${id}`}
        columns={columns}
        rows={rows}
        onRowClick={onGroupClicked}
        showActions={editClicked ? true : false}
        actionConfig={editClicked ? {
          onEdit: editClicked,
          entityName: "Group",
          pageName: "Messages",
          tabName: "Groups",
        } : undefined}
        useViewportHeight={false}
        containerSx={{ flexGrow: 1, marginTop: 1, minHeight: 320 }}
      />
    </Box>
  );
};

export default GroupTable;
